// ============================================================================
// OINP assessment copy.
// ============================================================================
// English is complete and is the fallback for every key. Other locales carry
// only the entry-point button labels for now: the wizard states legal
// thresholds (dollar figures, deadlines, CLB minimums) and shipping machine
// translations of those is how a candidate ends up acting on a wrong number.
// Add a locale by filling in the keys you have human translations for — `t()`
// falls back to English per key, not per locale, so partial locales are safe.
// ============================================================================

import { createI18n } from '../i18nCore';

const EN = {
  // ---- entry points -------------------------------------------------------
  oinpButton: 'Ontario PNP Score (EOI / 130)',
  employerButton: 'Is my employer eligible?',

  // ---- shared chrome -----------------------------------------------------
  loading: 'Loading…',
  // Shown when the standalone employer page is opened for an adviser whose
  // discipline does not offer the OINP employer check.
  employerUnavailable: 'This eligibility check is not available for this adviser. Please contact them directly.',
  stepOf: 'Step {current} of {total}',
  questionOf: 'Question {current} of {total}',
  back: '← Back',
  cancel: '← Cancel',
  next: 'Continue →',
  seeResults: 'See my results →',
  startOver: 'Start over',
  yes: 'Yes',
  no: 'No',
  notYet: 'Not yet',

  // ---- 01 path -----------------------------------------------------------
  pathTitle: 'Where are you starting from?',
  pathSubtitle: "Ontario's Workforce Priority stream is employer-initiated. There is a separate path for self-employed physicians.",
  pathEmployer: 'I have a job offer from an Ontario employer',
  pathEmployerDetail: "Or I'm about to receive one",
  pathPhysician: "I'm a self-employed physician in Ontario",
  pathPhysicianDetail: 'OHIP billing + CPSO certificate',
  pathNeither: 'Neither yet',
  pathNeitherDetail: "I'd like to learn more first",

  // ---- 02 NOC ------------------------------------------------------------
  nocTitle: "What's your occupation?",
  nocSubtitle: 'Your NOC 2021 code sets your TEER and broad occupational category.',
  nocSearchPlaceholder: 'Search your job title',
  nocSearching: 'Searching occupations…',
  nocNoResults: "No match for that title. Try a shorter or more common wording, or enter your 5-digit NOC code below.",
  nocMatchedOn: 'matches “{example}”',
  nocCodeLabel: 'Enter your 5-digit NOC code',
  nocApply: 'Apply',
  nocLookupLink: "Don't know your NOC? Find it on the Government of Canada NOC page",
  nocInvalid: 'A NOC 2021 code is exactly five digits.',
  nocNotFound: '{code} is not a NOC 2021 occupation code. Check it on the Government of Canada NOC page — a code that does not exist cannot be scored.',
  nocChecking: 'Checking…',
  nocResolved: 'NOC {code} — TEER {teer} · {category}',

  // ---- 03 region ---------------------------------------------------------
  regionTitle: 'Where is the job offer located?',
  regionSubtitle: 'Ontario spreads nominations across regions. Worth up to 15 points.',
  regionTitlePhysician: 'Where is your practice located?',
  regionSubtitlePhysician: 'Use your registered OHIP practice address. Ontario spreads nominations across regions — worth up to 15 points.',
  regionNorthern: 'Northern Ontario',
  regionEastern: 'Eastern Ontario',
  regionCentral: 'Central Ontario outside the GTA',
  regionSouthwestern: 'Southwestern Ontario',
  regionGtaExTo: 'GTA except Toronto',
  regionToronto: 'City of Toronto',
  regionPoints: '+{points} pts',

  // ---- 04 wage -----------------------------------------------------------
  wageTitle: "What's the hourly wage in your job offer?",
  wageSubtitle: 'Base hourly wage in CAD, before overtime, bonuses, commission or piece work.',
  wagePlaceholder: 'e.g. 28.50',
  wageBandsLabel: 'Point bands',
  wageInvalid: 'Enter the base hourly wage as a number.',

  // ---- 05 experience -----------------------------------------------------
  experienceTitle: 'Which of these describes your work history?',
  experienceSubtitle: "Pick the FIRST one that's true — this determines whether you meet the experience gate.",
  expSixMonths: '6+ months full-time in this exact position',
  expSixMonthsDetail: 'Same employer, same occupation, same location, within the last 12 months',
  expRecentGrad: 'Recent Ontario graduate + 3 months full-time in this position',
  expRecentGradDetail: "Graduated in the last 3 years (2+ year diploma, Ontario College Graduate Certificate, Master's or PhD) and 3 months full-time in this position within the last 12 months",
  expTwoYears: '2+ years cumulative in this occupation',
  expTwoYearsDetail: '3,120+ paid hours in the last 5 years. Part-time counts.',
  expLicensed: 'Licenced to practise in Ontario/Canada',
  expLicensedDetail: 'Your occupation legally requires a licence and you hold it. Truck (73300) and bus/transit (73301) drivers must still meet the 6-month rule.',
  expNone: 'None of the above yet',
  expNoneDetail: "You'll see what's needed to meet the gate on the results screen.",

  // ---- 06 months ---------------------------------------------------------
  monthsTitle: 'Time on the ground in Ontario',
  monthsSubtitle: 'Used for the Ontario work experience factor (up to 18 points).',
  monthsInPositionLabel: 'Months worked in the job offer position',
  monthsInOntarioLabel: 'Total months worked in Ontario (any job)',
  months05: '0–5 months',
  months612: '6–12 months',
  months1324: '13–24 months',
  months25: '25+ months',

  // ---- 07 education ------------------------------------------------------
  educationTitle: 'Your highest credential',
  eduDoctorate: 'Doctorate / professional degree',
  eduMasters: "Master's",
  eduUnivAboveBachelor: 'University certificate above bachelor',
  eduBachelor: "Bachelor's",
  eduOntarioGradCert: 'Ontario College Graduate Certificate',
  eduUnivBelowBachelor: 'University certificate below bachelor',
  eduCollegeDiploma: 'College certificate / diploma',
  eduApprenticeship: 'Apprenticeship / trades certificate',
  eduHighSchool: 'High school',
  eduLessThanHighSchool: 'Less than high school',
  canadianCredsLabel: 'Canadian post-secondary credentials (1+ yr, eligible institution)',
  ecaLabel: 'Educational Credential Assessment (ECA) less than 5 years old?',

  // ---- 08 language -------------------------------------------------------
  languageTitle: 'Language ability',
  languageSubtitle: 'Your LOWEST CLB across reading, writing, listening and speaking, on your best test. The test must be less than 2 years old. Academic and One Skill Retake tests are not accepted.',
  clb4OrLower: 'CLB 4 or lower',
  clb5: 'CLB 5',
  clb6: 'CLB 6',
  clb7: 'CLB 7',
  clb8: 'CLB 8',
  clb9Plus: 'CLB 9+',
  clbUntested: "Haven't tested",
  bothLanguagesLabel: 'Tested in both official languages at CLB 6+ in all four skills?',
  bothLanguagesYes: 'Yes — both tests, CLB 6+ in all skills',

  // ---- 09 status ---------------------------------------------------------
  statusTitle: 'Your status & Canadian earnings',
  statusLabel: 'Current status in Canada',
  statusWorkPermit: 'Valid work permit',
  statusStudyPermit: 'Valid study permit',
  statusMaintained: 'Maintained status',
  statusMaintainedDetail: 'Applied to extend before expiry',
  statusVisitor: 'Visitor',
  statusNone: 'No status',
  statusOutside: 'Outside Canada',
  earningsLabel: 'Highest single-year Canadian employment earnings (last 5 yrs, per CRA NOA)',
  earnings70k: '$70k+',
  earnings50to70: '$50,000 – $69,999',
  earnings30to50: '$30,000 – $49,999',
  earningsUnder30: 'Under $30,000',
  earningsNone: 'None',

  // ---- physician branch --------------------------------------------------
  ohipTitle: 'Do you have an OHIP billing number?',
  cpsoTitle: 'What CPSO certificate do you hold?',
  cpsoSubtitle: 'Postgraduate education licences do NOT qualify.',
  cpsoIndependent: 'Independent practice',
  cpsoAcademic: 'Academic',
  cpsoProvisional: 'Provisional',
  cpsoNone: 'None / postgraduate education licence only',
  practiceTitle: 'Months of cumulative medical practice in Ontario',

  // ---- no-offer branch ---------------------------------------------------
  neitherTitle: 'This stream needs a job offer first',
  neitherBody: "Ontario's Workforce Priority stream is employer-initiated — the employer submits the job offer through the Employer Portal before you can register your Expression of Interest. Self-employed physicians are the only exception.",
  neitherCta: 'Talk to us about how to land an Ontario job offer',

  // ---- results: eligibility ---------------------------------------------
  resultsTitle: 'Your Ontario PNP assessment',
  resultsSubtitle: 'Ontario Workforce Priority stream · {legalBasis} · rules dated {version}',
  eligibilityHeading: 'Eligibility',
  verdictEligible: 'Based on your answers, you appear to meet the stream requirements.',
  verdictNeedsVerification: 'You may meet the stream requirements — some answers need verifying.',
  verdictNotEligible: "Based on your answers, you don't meet the stream requirements yet.",
  verdictNeedsJobOffer: 'You need an Ontario job offer before this stream is open to you.',
  gateExperience: 'Work experience requirement',
  gateLanguage: 'Language minimum (CLB 6)',
  gateCredential: '1+ year post-secondary credential',
  gateEca: 'Educational Credential Assessment (if foreign)',
  gateStatus: 'Legal status in Canada',
  gateOhip: 'OHIP billing number',
  gateCpso: 'CPSO certificate (independent, academic or provisional)',
  gateOntarioPractice: 'Medical practice in Ontario',

  // ---- results: score ----------------------------------------------------
  scoreHeading: 'Estimated EOI score',
  scoreHeadingBlocked: 'Score if you qualified',
  scoreBlockedTitle: 'This score is not available to you yet',
  scoreBlockedBody: 'Your points are good, but the stream has {count} requirement(s) you do not currently meet. A score only counts once you are eligible — resolve the item(s) below and this score becomes achievable:',
  scoreOf: 'of {max}',
  breakdownHeading: 'Score breakdown',
  strongest: 'STRONGEST',
  factorTeer: 'TEER category',
  factorOccupation: 'Occupational category',
  factorWage: 'Hourly wage',
  factorOntarioExp: 'Ontario work experience',
  factorEarnings: 'Canadian earnings (best NOA year)',
  factorStatus: 'Legal status in Canada',
  factorEducation: 'Highest education',
  factorCanadianCreds: 'Canadian post-secondary credentials',
  factorClb: 'Lowest CLB (best test)',
  factorBilingual: 'Official languages known',
  factorRegion: 'Job offer region',

  scoreUnknownNocTitle: 'We could not score your occupation',
  scoreUnknownNocBody: 'The code you entered is not a NOC 2021 occupation, so your TEER and occupational category cannot be determined and no total can be shown. Re-run the check with your real NOC code and everything else you answered still applies.',
  scoreUnavailableTitle: 'Your score is not ready to publish',
  scoreUnavailableBody: "We won't show you a number we can't stand behind. {count} of the scoring factors is still unanswered or has no verified points table, so any total would be a guess. Your eligibility check above is unaffected, and every factor we can score is listed below.",
  factorPending: 'not yet scoreable',
  factorNotApplicable: 'n/a',
  physicianWageNote: 'The hourly wage factor does not apply to self-employed physicians, so your score is out of {max} rather than 130.',

  // ---- results: boosts ---------------------------------------------------
  boostHeading: 'Boost your score',
  boostSubtitle: 'The realistic changes that move your EOI score the most.',
  boostClb: 'Improve your lowest CLB to 9+ (from CLB {from})',
  boostRegion: 'The same job in {to} instead of {from}',
  boostBilingual: 'Add a second official language test at CLB 6+ in all four skills',
  boostCredential: 'Add a 2nd eligible Canadian credential (1+ yr)',
  boostWage: 'Negotiate the wage to {target}/hr',
  boostPoints: '+{gain} pts',
  regionalInsight: 'The same job offer scores 0 regionalization points in Toronto and 15 in Northern Ontario. Location is worth up to 15 points.',

  // ---- results: deadlines ------------------------------------------------
  deadlinesHeading: 'Deadlines to know',
  dlEmployerFirst: 'Your employer submits the job offer in the Employer Portal first.',
  dlRegisterEoi: 'You then have 30 days to register your Expression of Interest.',
  dlEoiValidity: 'The EOI stays valid up to 12 months. If no invitation comes, both it and the job offer expire.',
  dlEmployerApplication: 'If invited, your employer has 14 calendar days to submit the application for approval of the employment position.',
  dlCandidateApplication: 'You get 17 days in total — and you cannot submit until your employer has.',
  dlIrccApplication: 'After nomination: 6 months to apply to IRCC.',
  dlExpressEntry: 'Express Entry nominees: 30 days to accept the nomination (+600 CRS), then 60 days to apply.',

  // ---- results: CTAs -----------------------------------------------------
  employerCtaHeading: 'Is your employer eligible?',
  employerCtaBody: 'Your nomination depends on your employer qualifying and submitting first. Check their side in 2 minutes, or send them their own checklist.',
  employerCtaCheck: 'Check my employer now',
  employerCtaShare: 'Send my employer their checklist',
  employerChecklistHeading: 'The full employer process, end to end',
  printScorecard: 'Download my score card (PDF)',
  printChecklist: 'Download this checklist (PDF)',
  printBlocked: 'Your browser blocked the print window. Allow pop-ups for this site and try again.',

  // ---- draw / cutoff alerts ----------------------------------------------
  alertsHeading: 'Get told when cutoffs publish',
  alertsBody: 'No cutoff scores have been published for this stream yet — the EOI system is newly live. We will email you when draw results and cutoffs come out, with your score compared against them.',
  alertsPlaceholder: 'you@example.com',
  alertsSubscribe: 'Notify me',
  alertsDone: "You're on the list. We'll email you when results publish.",
  alertsInvalidEmail: 'Enter a valid email address.',
  // ---- continue to consultation ------------------------------------------
  // Finishing the assessment IS finishing a questionnaire, so this hands the
  // whole thing over rather than starting another round of questions.
  consultHeading: 'Continue to consultation',
  consultBody: 'Everything you answered goes straight to {expert} along with these results — you will not be asked any of it again. They will review your case and come back to you with next steps.',
  consultFallbackExpert: 'our team',
  // imavenai.com (?embed=site): the next step is iMaven's AI program check.
  siteConsultHeading: 'Next: your free AI program check',
  siteConsultBody: "iMaven's AI immigration agent picks up from these answers — you will not be asked them again — and walks you through the programs worth a look and what to check next. Free, general information.",
  siteContinueCta: 'Continue with the free AI program check',
  consultNotePlaceholder: 'Anything specific you want them to look at? (optional)',
  consultDone: 'Sent. Your answers and results have gone to the team — they will come back to you with next steps on this application.',

  continueCta: 'Continue to consultation',

  // ---- assessment-first landing (ad traffic, no identification up front) ---
  captureTitle: 'Almost there — how can they reach you?',
  captureBody: 'Send your results to {expert} and they will review your case and come back to you with next steps. We only need this to get back to you.',
  captureName: 'Full name',
  captureEmail: 'Email address',
  capturePhone: 'Phone number (optional)',
  captureSubmit: 'Send my results to an adviser',
  captureSending: 'Sending…',
  captureNameRequired: 'Please enter your name.',
  captureEmailRequired: 'Please enter a valid email address.',
  captureFailed: 'Something went wrong sending your results. Please try again.',
  captureDoneTitle: 'Sent — your case is with the team',
  captureDoneBody: 'Your answers and results have gone through. The next step is to set up your account, where you can book a time and share documents securely.',
  captureDoneCta: 'Set up my account and book a time',
  captureDeclined: 'No problem — your results are yours to keep. You can come back to this link any time, or reach out when you are ready.',
  // Third option: the Ontario check was a detour from why they actually came.
  resumeOriginalCta: 'Not about Ontario — carry on with my original question',
  declineCta: 'Not now, thank you',
  declineNote: 'One of our team members will reach out with next steps based on this assessment.',
  disclaimer: 'Informational only — not legal advice. Results are estimates. Where these results differ from an official OINP or IRCC decision, the official decision governs.',
  provenanceSecondary: 'Point values in this tool are pending verification against the official published grid.',

  // ---- employer checker --------------------------------------------------
  employerTitle: 'Is your business eligible to nominate?',
  employerSubtitle: 'Nine quick questions to see whether the business appears to meet the OINP employer requirements. About 2 minutes.',
  empQLocation: 'Where will the employee work (or report to work)?',
  empQLocationHelp: 'If the employee works at multiple locations, use the location where their supervisor works or where they receive work assignments.',
  locGta: 'Greater Toronto Area',
  locGtaDetail: 'City of Toronto, Durham, Halton, Peel, York',
  locMidsize: 'One of the 14 listed mid-size areas',
  locMidsizeDetail: 'Ottawa, Waterloo, Hamilton, Simcoe County, Middlesex, Niagara, Essex, Wellington, Greater Sudbury, Frontenac, Peterborough, Brant, Hastings, Thunder Bay',
  locElsewhere: 'Anywhere else in Ontario',
  locElsewhereDetail: 'Rural / smaller census divisions',

  empQYears: 'Has the business been in active business for at least 3 years?',
  empYearsPurchased: 'It was purchased or amalgamated within 3 years',
  empYearsPurchasedDetail: "The predecessor's continuous operation can count if the same products or services continued.",

  empQRevenue: 'Was gross annual revenue at least {revenueFloor} in EACH of the two most recently completed fiscal years?',
  empRevenueClose: 'Close to it',
  empRevenueCloseDetail: 'Fiscal year timing matters — check exact figures before deciding.',

  empQEmployees: 'Are there at least {minEmployees} full-time (30+ hrs/wk) employees who are Canadian citizens or permanent residents at this location?',
  empQEmployeesHelp: "Don't count part-timers, temporary foreign workers, or the employee being nominated.",

  empQOrders: 'Any outstanding orders under the Employment Standards Act or Occupational Health and Safety Act?',
  empQOrdersHelp: "'Outstanding' means unresolved — a resolved past order is not automatically disqualifying.",

  empQFullTime: 'Is the job offer full-time (30+ hrs/wk, 1,560+ hrs/yr) AND permanent (no end date)?',
  empQFullTimeHelp: 'Seasonal and fixed-term contracts are ineligible.',

  empQWage: 'Is the wage at or above the required wage level for the region?',
  empQWageHelp: "Base hourly wage only — bonuses, commission, piece work and vacation pay don't count. If the employee already works there, the offer must also be at or above their current wage.",
  empWageMedian: 'Yes, at or above the median wage',
  empWageRecentGrad: 'Recent Ontario graduate (TEER 0–3) and at or above the low wage level',
  empWageCollective: 'The wage is set by a collective agreement (exempt)',
  empWageNotSure: 'Not sure — verify before applying',

  empQAuthorized: 'Is the employee already authorized to work in Ontario, OR is there a positive LMIA for this exact position?',
  empQAuthorizedHelp: 'If yes, the domestic recruitment requirement does not apply.',

  empQCvor: 'Is the position NOC 73300 (transport truck driver) or 73301 (bus/transit operator)?',
  empCvorYes: 'Yes — and there is a valid CVOR (Excellent or Satisfactory)',
  empCvorNo: 'Yes — but no valid CVOR at the required rating',

  // ---- employer results: short requirement labels for the verdict list ----
  empReqLocation: 'Work location',
  empReqYears: '3+ years in active business',
  empReqRevenue: 'Revenue threshold for this location',
  empReqEmployees: 'Full-time Canadian citizen / PR employees at the location',
  empReqOrders: 'No outstanding ESA or OHSA orders',
  empReqFullTime: 'Full-time, permanent job offer',
  empReqWage: 'Required wage level',
  empReqRecruitment: 'Recruitment requirement',
  empReqCvor: 'CVOR (truck and bus/transit positions)',

  // ---- employer results --------------------------------------------------
  empVerdictEligible: 'This business appears to meet the OINP employer requirements.',
  empVerdictNeedsVerification: 'This business may qualify — some items need verifying first.',
  empVerdictNotEligible: "This business doesn't meet the employer requirements for this stream yet.",
  empTierNote: 'Thresholds applied for this work location: revenue {revenueFloor} in each of the two most recent fiscal years, and at least {minEmployees} full-time Canadian citizen or PR employees at the location.',
  empFailYears: 'The business must have been in active business for at least 3 years.',
  empWarnYears: "Confirm the predecessor's continuous operation covers the full 3 years and the same products or services continued.",
  empFailRevenue: 'Gross annual revenue is below the threshold for this work location.',
  empWarnRevenue: 'Revenue is borderline — confirm the exact figures for both completed fiscal years.',
  empFailEmployees: 'There are not enough full-time Canadian citizen or PR employees at this work location.',
  empFailOrders: 'Outstanding ESA or OHSA orders must be resolved first.',
  empFailFullTime: 'The job offer must be full-time and permanent — seasonal and fixed-term are ineligible.',
  empWarnWage: 'Confirm the required wage level for this position and region before submitting.',
  empWarnRecruitment: 'Domestic recruitment is required before the offer: at least 2 advertising methods, posted 4+ weeks, with the required ad content — and never conducted by an immigration representative.',
  empFailCvor: 'Truck and bus/transit positions require a valid CVOR rated Excellent or Satisfactory.',
  empChecklistCta: 'Email me the full employer checklist (PDF)',
  empAdviserCta: 'Send this to my immigration adviser',
  empCaptureEmail: 'you@company.com',
  empCaptureSend: 'Send it',
  empCaptureDone: "Sent. Check your inbox — we've included the full portal walkthrough.",

  // ---- notes -------------------------------------------------------------
  licenceRouteBlockedForDrivers: 'Truck (73300) and bus/transit (73301) drivers must still meet the 6-month rule even when licenced.',
  skilledTradesLanguageException: 'Your score is below the CLB 6 minimum for most TEER 0–3 occupations, but skilled trades qualify at CLB 5. Worth confirming whether your occupation is on the skilled trades list.',
  skilledTradesEducationException: 'Most TEER 0–3 occupations need a post-secondary credential of at least one year, but skilled trades and TEER 4–5 occupations need only a secondary school diploma. Worth confirming which applies to your occupation.',
  noStatusNeedsAdvice: 'Being in Canada without status does not by itself disqualify you from this stream, but it needs advice before you apply.',
  legalStatusScoresZeroButEligible: 'This status earns no points for the legal status factor, but it is not an eligibility barrier on its own.',
  recentGradLanguageExempt: 'Recent eligible Ontario graduates are exempt from the language test requirement for eligibility. Test scores still earn points, so submit them if you have them.',
  pswEducationNote: 'For nurse aides, orderlies and personal support workers (NOC 33102) an Ontario college certificate in a related field satisfies the education requirement.',
  postgradLicenceDoesNotQualify: 'A postgraduate education licence does not qualify — an independent, academic or provisional CPSO certificate is required.',
};

// Navigation and form chrome only — words whose meaning does not depend on a
// legal threshold, so translating them carries no risk of a candidate acting on a
// wrong figure. Everything substantive (dollar amounts, deadlines, CLB minimums,
// eligibility wording) stays English until a human translator reviews it, and
// `t()` falls back per key so these partial locales are safe to ship.
const CHROME = {
  fr: { back: '← Retour', cancel: '← Annuler', next: 'Continuer →', seeResults: 'Voir mes résultats →', startOver: 'Recommencer', yes: 'Oui', no: 'Non', notYet: 'Pas encore', stepOf: 'Étape {current} sur {total}', questionOf: 'Question {current} sur {total}', alertsSubscribe: 'Prévenez-moi', alertsPlaceholder: 'vous@exemple.com' },
  es: { back: '← Atrás', cancel: '← Cancelar', next: 'Continuar →', seeResults: 'Ver mis resultados →', startOver: 'Empezar de nuevo', yes: 'Sí', no: 'No', notYet: 'Aún no', stepOf: 'Paso {current} de {total}', questionOf: 'Pregunta {current} de {total}', alertsSubscribe: 'Avísame', alertsPlaceholder: 'tu@ejemplo.com' },
  pt: { back: '← Voltar', cancel: '← Cancelar', next: 'Continuar →', seeResults: 'Ver meus resultados →', startOver: 'Começar de novo', yes: 'Sim', no: 'Não', notYet: 'Ainda não', stepOf: 'Etapa {current} de {total}', questionOf: 'Pergunta {current} de {total}', alertsSubscribe: 'Avise-me', alertsPlaceholder: 'voce@exemplo.com' },
  he: { back: '→ חזרה', cancel: '→ ביטול', next: '← המשך', seeResults: '← הצג את התוצאות שלי', startOver: 'התחל מחדש', yes: 'כן', no: 'לא', notYet: 'עדיין לא', stepOf: 'שלב {current} מתוך {total}', questionOf: 'שאלה {current} מתוך {total}', alertsSubscribe: 'עדכנו אותי', alertsPlaceholder: 'you@example.com' },
  ar: { back: '→ رجوع', cancel: '→ إلغاء', next: '← متابعة', seeResults: '← اعرض نتائجي', startOver: 'ابدأ من جديد', yes: 'نعم', no: 'لا', notYet: 'ليس بعد', stepOf: 'الخطوة {current} من {total}', questionOf: 'السؤال {current} من {total}', alertsSubscribe: 'أبلغني', alertsPlaceholder: 'you@example.com' },
  zh: { back: '← 返回', cancel: '← 取消', next: '继续 →', seeResults: '查看我的结果 →', startOver: '重新开始', yes: '是', no: '否', notYet: '还没有', stepOf: '第 {current} 步，共 {total} 步', questionOf: '第 {current} 题，共 {total} 题', alertsSubscribe: '通知我', alertsPlaceholder: 'you@example.com' },
  ru: { back: '← Назад', cancel: '← Отмена', next: 'Продолжить →', seeResults: 'Показать результаты →', startOver: 'Начать заново', yes: 'Да', no: 'Нет', notYet: 'Пока нет', stepOf: 'Шаг {current} из {total}', questionOf: 'Вопрос {current} из {total}', alertsSubscribe: 'Сообщить мне', alertsPlaceholder: 'you@example.com' },
  hi: { back: '← पीछे', cancel: '← रद्द करें', next: 'जारी रखें →', seeResults: 'मेरे परिणाम देखें →', startOver: 'फिर से शुरू करें', yes: 'हाँ', no: 'नहीं', notYet: 'अभी नहीं', stepOf: 'चरण {current} / {total}', questionOf: 'प्रश्न {current} / {total}', alertsSubscribe: 'मुझे सूचित करें', alertsPlaceholder: 'you@example.com' },
  ko: { back: '← 뒤로', cancel: '← 취소', next: '계속 →', seeResults: '결과 보기 →', startOver: '다시 시작', yes: '예', no: '아니오', notYet: '아직 아니오', stepOf: '{total}단계 중 {current}단계', questionOf: '{total}개 중 {current}번째 질문', alertsSubscribe: '알려주세요', alertsPlaceholder: 'you@example.com' },
  tl: { back: '← Bumalik', cancel: '← Kanselahin', next: 'Magpatuloy →', seeResults: 'Tingnan ang resulta ko →', startOver: 'Magsimula muli', yes: 'Oo', no: 'Hindi', notYet: 'Hindi pa', stepOf: 'Hakbang {current} ng {total}', questionOf: 'Tanong {current} ng {total}', alertsSubscribe: 'Ipaalam sa akin', alertsPlaceholder: 'you@example.com' },
  fa: { back: '→ بازگشت', cancel: '→ لغو', next: '← ادامه', seeResults: '← نتایج من', startOver: 'شروع مجدد', yes: 'بله', no: 'خیر', notYet: 'هنوز نه', stepOf: 'مرحله {current} از {total}', questionOf: 'سؤال {current} از {total}', alertsSubscribe: 'به من خبر بده', alertsPlaceholder: 'you@example.com' },
  tr: { back: '← Geri', cancel: '← İptal', next: 'Devam →', seeResults: 'Sonuçlarımı gör →', startOver: 'Baştan başla', yes: 'Evet', no: 'Hayır', notYet: 'Henüz değil', stepOf: 'Adım {current} / {total}', questionOf: 'Soru {current} / {total}', alertsSubscribe: 'Beni bilgilendir', alertsPlaceholder: 'siz@ornek.com' },
  uk: { back: '← Назад', cancel: '← Скасувати', next: 'Продовжити →', seeResults: 'Показати результати →', startOver: 'Почати заново', yes: 'Так', no: 'Ні', notYet: 'Ще ні', stepOf: 'Крок {current} з {total}', questionOf: 'Питання {current} з {total}', alertsSubscribe: 'Повідомити мене', alertsPlaceholder: 'you@example.com' },
  vi: { back: '← Quay lại', cancel: '← Hủy', next: 'Tiếp tục →', seeResults: 'Xem kết quả của tôi →', startOver: 'Bắt đầu lại', yes: 'Có', no: 'Không', notYet: 'Chưa', stepOf: 'Bước {current} / {total}', questionOf: 'Câu hỏi {current} / {total}', alertsSubscribe: 'Thông báo cho tôi', alertsPlaceholder: 'you@example.com' },
  ja: { back: '← 戻る', cancel: '← キャンセル', next: '続ける →', seeResults: '結果を見る →', startOver: '最初からやり直す', yes: 'はい', no: 'いいえ', notYet: 'まだです', stepOf: 'ステップ {current} / {total}', questionOf: '質問 {current} / {total}', alertsSubscribe: '通知する', alertsPlaceholder: 'you@example.com' },
  ur: { back: '→ واپس', cancel: '→ منسوخ', next: '← جاری رکھیں', seeResults: '← میرے نتائج دیکھیں', startOver: 'دوبارہ شروع کریں', yes: 'ہاں', no: 'نہیں', notYet: 'ابھی نہیں', stepOf: 'مرحلہ {current} از {total}', questionOf: 'سوال {current} از {total}', alertsSubscribe: 'مجھے مطلع کریں', alertsPlaceholder: 'you@example.com' },
  it: { back: '← Indietro', cancel: '← Annulla', next: 'Continua →', seeResults: 'Vedi i miei risultati →', startOver: 'Ricomincia', yes: 'Sì', no: 'No', notYet: 'Non ancora', stepOf: 'Passo {current} di {total}', questionOf: 'Domanda {current} di {total}', alertsSubscribe: 'Avvisami', alertsPlaceholder: 'tu@esempio.com' },
};

const ENTRY_BUTTONS = {
  fr: { oinpButton: 'Score PNP Ontario (DI / 130)', employerButton: 'Mon employeur est-il admissible ?' },
  es: { oinpButton: 'Puntaje PNP de Ontario (EOI / 130)', employerButton: '¿Mi empleador es elegible?' },
  pt: { oinpButton: 'Pontuação PNP de Ontário (EOI / 130)', employerButton: 'Meu empregador é elegível?' },
  he: { oinpButton: 'ניקוד PNP אונטריו (EOI / 130)', employerButton: 'האם המעסיק שלי כשיר?' },
  ar: { oinpButton: 'نقاط PNP أونتاريو (EOI / 130)', employerButton: 'هل صاحب العمل مؤهل؟' },
  zh: { oinpButton: '安省 PNP 分数（EOI / 130）', employerButton: '我的雇主符合条件吗？' },
  ru: { oinpButton: 'Балл PNP Онтарио (EOI / 130)', employerButton: 'Подходит ли мой работодатель?' },
  hi: { oinpButton: 'ओंटारियो PNP स्कोर (EOI / 130)', employerButton: 'क्या मेरा नियोक्ता योग्य है?' },
  ko: { oinpButton: '온타리오 PNP 점수 (EOI / 130)', employerButton: '고용주가 자격이 되나요?' },
  tl: { oinpButton: 'Ontario PNP Score (EOI / 130)', employerButton: 'Kwalipikado ba ang employer ko?' },
  fa: { oinpButton: 'امتیاز PNP انتاریو (EOI / 130)', employerButton: 'آیا کارفرمای من واجد شرایط است؟' },
  tr: { oinpButton: 'Ontario PNP Puanı (EOI / 130)', employerButton: 'İşverenim uygun mu?' },
  uk: { oinpButton: 'Бал PNP Онтаріо (EOI / 130)', employerButton: 'Чи підходить мій роботодавець?' },
  vi: { oinpButton: 'Điểm PNP Ontario (EOI / 130)', employerButton: 'Chủ lao động của tôi có đủ điều kiện?' },
  ja: { oinpButton: 'オンタリオ PNP スコア (EOI / 130)', employerButton: '雇用主は要件を満たしますか？' },
  ur: { oinpButton: 'اونٹاریو PNP اسکور (EOI / 130)', employerButton: 'کیا میرا آجر اہل ہے؟' },
  it: { oinpButton: 'Punteggio PNP Ontario (EOI / 130)', employerButton: 'Il mio datore di lavoro è idoneo?' },
};

const LOCALES = { en: EN };
for (const code of Object.keys(ENTRY_BUTTONS)) {
  LOCALES[code] = { ...ENTRY_BUTTONS[code], ...(CHROME[code] || {}) };
}

const { t, translator, supported } = createI18n(LOCALES);

export { t, translator };
export const SUPPORTED_OINP_LOCALES = supported;
